import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard } from '../../components/AuthGuard';
import CentralLayout from '../../layouts/CentralLayout';
import SiteManagementPage from './settings/sites/SiteManagementPage';
import SiteMapPage from './settings/sites/SiteMapPage';
import TransitManagementPage from '../tms/transit/TransitManagementPage';
import { ZipcodesPage } from './settings/ZipcodesPage';
import CentralReportsPage from './reports/ReportsPage';
import CentralProgressPage from './progress/ProgressPage';
import CentralAccountPage from './account/AccountPage';
import CentralDspPage from './dsp/DspPage';
import CentralChannelsPage from './channels/ChannelsPage';
import { WMS_DELIVERY_MONITOR_PATH } from '../wms/routePaths';
import CreateOrderPage from './parcels/CreateOrderPage';
import CentralReportFilePage from './download/CentralReportFilePage';
import CentralParcelsPage from './parcels/CentralParcelsPage';
import CentralParcelHistoryPage from './parcels/CentralParcelHistoryPage';
import DamagedLabelLookupPage from '../damaged-label-lookup/DamagedLabelLookupPage';
import TrackExportPage from './parcels/track-export/TrackExportPage';
import VehicleModelPage from './vehicle-model/VehicleModelPage';
import CustomsClearancePage from './customs-clearance/CustomsClearancePage';
import PodUploadPage from './pod-upload/PodUploadPage';
import AccountsPage from "../delivery/accounts/AccountsPage";
import { ACCOUNTS_PAGE_MODE } from "../delivery/accounts/hooks/useAccountDeliveryDriverQuery";
import SensitiveWordsTestPage from './test/SensitiveWordsTestPage';
import ZoneMappingPage from '../pricing/zone-mapping/ZoneMappingPage';
import OperationLogPage from './operation-logs/OperationLogPage';
import RelationChangeLogPage from './relation-logs/RelationChangeLogPage';
import AddressBlacklistTestPage from './test/AddressBlacklistTestPage';
import AccessCodeImportPage from './test/AccessCodeImportPage';
import ClientHubZonePage from './settings/client-hub-zone/ClientHubZonePage';
import DefaultZipcodePickupHubPage from './settings/default-zipcode-pickup-hub/DefaultZipcodePickupHubPage';
import CarrierPage from "./carrier/CarrierPage";
import HandoverCarrierPage from "./handover-carrier/HandoverCarrierPage";
import AddressMetadataTestPage from './test/AddressMetadataTestPage';
import ResourcePage from './platform-permission/resource/ResourcePage';
import PermissionPackagePage from './platform-permission/permission-package/PermissionPackagePage';
import OrganizationStructurePage from './platform-permission/organization/OrganizationStructurePage';
import PermissionOperationLogPage from './platform-permission/operation-log/PermissionOperationLogPage';
import RolePage from './platform-permission/role/RolePage';
import PersonnelPage from './platform-permission/personnel/PersonnelPage';
import LocationCorrectionPage from '../map-tool/location-correction/LocationCorrectionPage';
import GeofenceBlacklistReviewPage from '../map-tool/geofence-blacklist-review/GeofenceBlacklistReviewPage';
import AbAddressReviewPage from '../map-tool/ab-address-review/AbAddressReviewPage';
import DriverLocationTrackPage from '../map-tool/driver-location-track/DriverLocationTrackPage';
import StandardizedAddressPage from '../map-tool/standardized-address/StandardizedAddressPage';
import AddressLifecyclePage from '../map-tool/address-lifecycle/AddressLifecyclePage';
import AddressResidentialAreaPage from '../map-tool/address-residential-area/AddressResidentialAreaPage';
import AddressResidentialAreaPendingPage from '../map-tool/address-residential-area/AddressResidentialAreaPendingPage';
import AddressCoordDedupControlPage from "../support-tools/address-coord-dedup/AddressCoordDedupControlPage";
import SupportLoginPage from '../support-tools/support-login/SupportLoginPage';
import LoginLogPage from '../support-tools/login-log/LoginLogPage';
import RedisToolsPage from '../support-tools/redis-tools/RedisToolsPage';
import ManualOrderPoolPage from '../support-tools/order-pool/ManualOrderPoolPage';
import SplitRouteCachePage from '../support-tools/split-route-cache/SplitRouteCachePage';
import EsToolsPage from '../support-tools/es-tools/EsToolsPage';
import UploadToolPage from '../support-tools/upload/UploadToolPage';
import DubboToolsPage from '../support-tools/dubbo-tools/DubboToolsPage';
import ReflectInvokePage from '../support-tools/reflect-invoke/ReflectInvokePage';
import CommonConfigPage from '../config/common-config/CommonConfigPage';
import PodCaptureConfigPage from '../config/pod-capture-config/PodCaptureConfigPage';
import PodCaptureConfigV2Page from '../config/pod-capture-config-v2/PodCaptureConfigV2Page';
import WarehousePlanningEntryConfigPage from '../config/entry-config/WarehousePlanningEntryConfigPage';
import DegradeManagementPage from '../config/degrade/DegradeManagementPage';
import AppVersionPage from '../config/app-version/AppVersionPage';
import PdaScanConfigPage from '../config/pda-scan-config/PdaScanConfigPage';
import LearningCenterConfigPage from '../config/learning-center-config/LearningCenterConfigPage';
import PerformanceMonitor from '../map-tool/performance-monitor/PerformanceMonitor';
import AddressWhitelistTabsPage from "../map-tool/address-whitelist/AddressWhitelistTabsPage";
import AddressWhitelistRulePage from "../map-tool/address-whitelist-rule/AddressWhitelistRulePage";
import AddressBlacklistPage from "../map-tool/address-blacklist/AddressBlacklistPage";
import AddressInvalidTabsPage from "../map-tool/address-invalid/AddressInvalidTabsPage";
import PdaHomeConfigPage from './pda-home-config/PdaHomeConfigPage';
import AoiPlatformDetailPage from "../map-tool/aoi-platform/AoiPlatformDetailPage";

import AoiPlatformV2Page from "../map-tool/aoi-platform-v2/AoiPlatformV2Page";

const CentralRoutes: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<AuthGuard><CentralLayout /></AuthGuard>}>
                <Route path="settings" element={<Navigate to="sites" replace />} />
                <Route path="settings/sites" element={<Navigate to="management" replace />} />
                <Route path="settings/sites/management" element={<SiteManagementPage />} />
                <Route path="settings/sites/map" element={<SiteMapPage />} />
                <Route path="settings/sites/transit" element={<TransitManagementPage />} />
                <Route path="settings/sites/zipcodes" element={<ZipcodesPage />} />
                <Route path="settings/zipcodes" element={<Navigate to="/central/settings/sites/zipcodes" replace />} />
                <Route path="settings/client-hub-zone" element={<ClientHubZonePage />} />
                <Route path="settings/default-zipcode-pickup-hub" element={<DefaultZipcodePickupHubPage />} />
                <Route path="reports" element={<CentralReportsPage />} />
                <Route path="progress" element={<CentralProgressPage />} />
                <Route path="parcels" element={<CentralParcelsPage />} />
                <Route path="parcels/history" element={<CentralParcelHistoryPage />} />
                <Route path="parcels/damaged-label-lookup" element={<DamagedLabelLookupPage />} />
                <Route path="parcels/track-export" element={<TrackExportPage />} />
                <Route path="parcels/create" element={<CreateOrderPage />} />
                <Route path="account" element={<CentralAccountPage />} />
                <Route path="dsp" element={<CentralDspPage />} />
                <Route path="delivery-driver" element={<AccountsPage mode={ACCOUNTS_PAGE_MODE.CENTRAL} />} />
                <Route path="channels" element={<CentralChannelsPage />} />
                {/* 派送监控已挪到 WMS，旧地址重定向过去，收藏的链接不失效 */}
                <Route path="delivery-monitor" element={<Navigate to={WMS_DELIVERY_MONITOR_PATH} replace />} />
                <Route path="download" element={<CentralReportFilePage />} />
                <Route path="vehicle-model" element={<VehicleModelPage />} />
                <Route path="customs-clearance" element={<CustomsClearancePage />} />
                {/*<Route path="pod-upload" element={<PodUploadPage />} />*/}
                <Route path="pricing/zone-mapping" element={<ZoneMappingPage />} />
                <Route path="test/sensitive-words" element={<SensitiveWordsTestPage />} />
                <Route path="operation-logs" element={<OperationLogPage />} />
                <Route path="relation-logs" element={<RelationChangeLogPage />} />
                <Route path="test/address-blacklist" element={<AddressBlacklistTestPage />} />
                <Route path="test/address-metadata" element={<AddressMetadataTestPage />} />
                <Route path="test/access-code-import" element={<AccessCodeImportPage />} />
                <Route path="carrier" element={<CarrierPage />} />
                <Route path="handover-carrier" element={<HandoverCarrierPage />} />
                <Route path="platform-permission/resource" element={<ResourcePage />} />
                <Route path="platform-permission/permission-package" element={<PermissionPackagePage />} />
                <Route path="platform-permission/role" element={<RolePage />} />
                <Route path="platform-permission/personnel" element={<PersonnelPage />} />
                <Route path="platform-permission/organization" element={<OrganizationStructurePage />} />
                <Route path="platform-permission/operation-log" element={<PermissionOperationLogPage />} />
                <Route path="map-tool/location-correction" element={<LocationCorrectionPage />} />
                <Route path="map-tool/geofence-blacklist-review" element={<GeofenceBlacklistReviewPage />} />
                <Route path="map-tool/ab-address-review" element={<AbAddressReviewPage />} />
                <Route path="map-tool/standardized-address" element={<StandardizedAddressPage />} />
                <Route path="map-tool/address-lifecycle" element={<AddressLifecyclePage />} />
                <Route path="map-tool/address-whitelist" element={<AddressWhitelistTabsPage />} />
                <Route path="map-tool/address-whitelist-rule" element={<AddressWhitelistRulePage />} />
                <Route path="map-tool/address-blacklist" element={<AddressBlacklistPage />} />
                <Route path="map-tool/address-invalid" element={<AddressInvalidTabsPage />} />
                <Route path="map-tool/address-residential-area" element={<AddressResidentialAreaPage />} />
                <Route path="map-tool/address-residential-area-pending" element={<AddressResidentialAreaPendingPage />} />
                <Route path="map-tool/driver-location-track" element={<DriverLocationTrackPage />} />
                <Route path="map-tool/address-coord-dedup" element={<AddressCoordDedupControlPage />} />
                <Route
                    path="map-tool/aoi-platform"
                    element={
                        <AoiPlatformV2Page />
                    }
                />
                <Route path="map-tool/aoi-platform/:aoiId" element={<AoiPlatformDetailPage />} />
                <Route path="common-config" element={<CommonConfigPage />} />
                <Route path="pod-capture-config" element={<PodCaptureConfigPage />} />
                <Route path="pod-capture-config-v2" element={<PodCaptureConfigV2Page />} />
                <Route path="entry-config" element={<WarehousePlanningEntryConfigPage />} />
                <Route path="degrade" element={<DegradeManagementPage />} />
                <Route path="app-version" element={<AppVersionPage />} />
                <Route path="pda-scan-config" element={<PdaScanConfigPage />} />
                <Route path="performance-monitor" element={<PerformanceMonitor />} />
                <Route path="pda-home-config" element={<PdaHomeConfigPage />} />
                <Route path="learning-center-config" element={<LearningCenterConfigPage />} />
                <Route path="support-tools/support-login" element={<SupportLoginPage />} />
                <Route path="support-tools/login-log" element={<LoginLogPage />} />
                <Route path="support-tools/redis-tools" element={<RedisToolsPage />} />
                <Route path="support-tools/order-pool" element={<ManualOrderPoolPage />} />
                <Route path="support-tools/split-route-cache" element={<SplitRouteCachePage />} />
                <Route path="support-tools/es-tools" element={<EsToolsPage />} />
                <Route path="support-tools/upload" element={<UploadToolPage />} />
                <Route path="support-tools/dubbo-tools" element={<DubboToolsPage />} />
                <Route path="support-tools/reflect-invoke" element={<ReflectInvokePage />} />
            </Route>
         </Routes>
    );
};

export default CentralRoutes; 